import React, { useEffect, useRef } from 'react';
import { MdOutlineClose, MdOutlineErrorOutline, MdOutlineCheckCircle } from "react-icons/md";
import { HiOutlineInformationCircle } from "react-icons/hi2";


const SmallPopupTop = ({ message, isShowing, show, setShow, type, title, logText, durationMs = 7000, onDismiss, stacked = false }) => {
    const visible = typeof isShowing === 'boolean' ? isShowing : typeof show === 'boolean' ? show : false;
    const borderColour = type === 'error' ? 'border-red-500' : type === 'success' ? 'border-green-500' : type === 'info' ? 'border-blue-500' : type === 'warning' ? 'border-yellow-500' : null;
    const backgroundColour = type === 'error' ? 'bg-red-500' : type === 'success' ? 'bg-green-500' : type === 'info' ? 'bg-blue-500' : type === 'warning' ? 'bg-yellow-500' : null;
    const shouldAutoDismiss = visible && durationMs !== null && durationMs > 0;
    const dismissRef = useRef(onDismiss);
    const setShowRef = useRef(setShow);

    useEffect(() => {
        dismissRef.current = onDismiss;
        setShowRef.current = setShow;
    }, [onDismiss, setShow]);

    useEffect(() => {
        if (!shouldAutoDismiss) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            if (dismissRef.current) {
                dismissRef.current();
                return;
            }

            if (setShowRef.current) {
                setShowRef.current(false);
            }
        }, durationMs);

        return () => window.clearTimeout(timeoutId);
    }, [durationMs, shouldAutoDismiss]);

    const handleClose = () => {
        if (dismissRef.current) {
            dismissRef.current();
            return;
        }

        if (setShowRef.current) {
            setShowRef.current(false);
        }
    };

    // ${borderColour && borderColour + ' border-l-8'}
    // border ${borderColour && borderColour + ' border-l-8'}
    const containerClasses = stacked
        ? `relative w-full transition-all duration-300 ease-out ${visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0 pointer-events-none'}`
        : `text-text-secondary-dark bg-white shadow-sm fixed top-6 right-0 mr-3 transition-all duration-300 ease-out flex gap-5 items-center justify-between px-3 py-2 max-w-96 min-w-72 min-h-20 rounded-md border z-40 ${visible ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0 pointer-events-none'}`;

    return (
        <div className={stacked ? `${containerClasses} text-text-secondary-dark bg-white shadow-sm flex gap-5 items-center justify-between px-3 py-2 min-h-20 rounded-md pointer-events-auto border ${borderColour}` : containerClasses}>
            <div className="w-min shrink-0">
                {type === 'error' && <MdOutlineErrorOutline className='text-red-500 text-3xl' />}
                {type === 'success' && <MdOutlineCheckCircle className='text-green-500 text-3xl' />}
                {type === 'info' && <HiOutlineInformationCircle className='text-blue-500 text-3xl' />}
                {type === 'warning' && <HiOutlineInformationCircle className='text-yellow-500 text-3xl' />}
            </div>
            <div className="flex flex-col flex-grow">
                {title && <h1 className='font-semibold'>{title}</h1>}
                <p className='text-xs'>{message}</p>
                {logText &&
                    <button onClick={() => {
                        const element = document.createElement("a");
                        const file = new Blob([logText], { type: 'text/plain' });
                        element.href = URL.createObjectURL(file);
                        element.download = `error-log-${new Date().toISOString().split('T')[0]}.txt`;
                        document.body.appendChild(element); // Required for this to work in FireFox
                        element.click();
                        document.body.removeChild(element);
                    }} className='text-blue-500 align-top underline text-xs rounded-full h-min'>Save Error</button>
                }
            </div>
            <div className="w-min shrink-0">
                <button onClick={handleClose} className='hover:bg-slate-100 p-0.5 rounded-full h-min'><MdOutlineClose className='text-2xl text-text-secondary-reg' /></button>
            </div>
            {shouldAutoDismiss && (
                <div className="absolute bottom-0 left-0 h-1 w-full overflow-hidden rounded-b-md bg-slate-100">
                    <div
                        className={`h-full w-full origin-left bg-current small-popup-progress ${backgroundColour}`}
                        style={{
                            animationDuration: `${durationMs}ms`
                        }}
                    />
                </div>
            )}

        </div>
    );
};

export default SmallPopupTop;